import React, { useState } from "react";
import axios from "axios";
import { Check, TrendingUp, Clock, Shield, Calculator, Bell, ChevronRight, Star, Zap } from "lucide-react";
import { PublicFooter } from "../components/layout/PublicFooter";
import { QontoLogo } from "../components/integrations/QontoLogo";
import { QONTO_ENABLED } from "../utils/featureFlags";
import { API } from "../utils/constants";

const COLORS = {
  primary: '#210875',
  accent: '#897FCB',
  success: '#059669',
  warning: '#f59e0b',
  bg: '#FDFCFF',
  coral: '#E8A87C'
};

export const LandingPage = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      // Envoi à la liste d'attente backend (silencieux, best-effort)
      axios.post(`${API}/qonto/early-access`, { email }).catch(() => {});
    }
  };

  const features = [
    { icon: TrendingUp, title: "Suivi CA en temps réel", desc: "Visualise ton chiffre d'affaires et ta progression vers les seuils" },
    { icon: Calculator, title: "Calculs Urssaf automatiques", desc: "Cotisations calculées selon ton activité, ACRE inclus" },
    { icon: Bell, title: "Alertes intelligentes", desc: "Rappels TVA, échéances déclarations, seuils CFE" },
    { icon: Shield, title: "100% conforme 2026", desc: "Taux et seuils toujours à jour selon la réglementation" },
    { icon: Clock, title: "Gain de temps", desc: "2h/mois économisées sur ta gestion administrative" },
    { icon: Zap, title: "Simple et intuitif", desc: "Conçu par un auto-entrepreneur, pour les auto-entrepreneurs" }
  ];

  const testimonials = [
    { name: "Sabrina B.", role: "Graphiste freelance", text: "Enfin un outil qui comprend la réalité des auto-entrepreneurs ! Fini les calculs sur Excel." },
    { name: "Julien M.", role: "Consultant IT", text: "Le calcul automatique des cotisations m'a fait gagner un temps fou. Je recommande !" },
    { name: "Laura D.", role: "Coach sportif", text: "Les alertes TVA m'ont évité une mauvaise surprise. Merci Biz360 !" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#210875] via-[#897FCB] to-[#210875]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#E8A87C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Pilote ton auto-entreprise<br />
              <span className="text-[#E8A87C]">sans prise de tête</span>
            </h1>
            
            <p className="text-xl md:text-2xl opacity-90 mb-8 max-w-3xl mx-auto">
              L'outil qui calcule automatiquement tes cotisations Urssaf, suit ton CA et t'alerte avant les échéances. 
              <strong> Fini les tableaux Excel.</strong>
            </p>

            {/* CTA Form */}
            {!submitted ? (
              <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ton email pour être prévenu(e)"
                    required
                    className="flex-1 h-14 px-5 rounded-xl text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-white/30"
                  />
                  <button
                    type="submit"
                    className="h-14 px-8 bg-[#059669] hover:bg-[#047857] text-white font-bold rounded-xl transition-all hover:scale-105 whitespace-nowrap"
                  >
                    Je réserve ma place ✨
                  </button>
                </div>
              </form>
            ) : (
              <div className="max-w-md mx-auto mb-8 bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center justify-center gap-2 text-[#E8A87C] mb-2">
                  <Check className="w-6 h-6" />
                  <span className="font-bold text-lg">Place réservée !</span>
                </div>
                <p className="opacity-90">Tu recevras un email dès le lancement avec ton offre exclusive.</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Zap className="w-4 h-4 text-[#E8A87C]" />
                <span>💬 Mon email direct — réponse sous 24h</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <span>🇫🇷 Outil français, 100% conforme 2026</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar — supprimée (aucune vraie metric à afficher pour le moment) */}

      {/* Problem/Solution */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: COLORS.primary }}>
                Tu en as marre de...
              </h2>
              <ul className="space-y-4">
                {[
                  "Passer des heures sur Excel pour calculer tes cotisations",
                  "Avoir peur de te tromper dans tes déclarations Urssaf",
                  "Découvrir trop tard que tu as dépassé un seuil TVA",
                  "Ne jamais savoir exactement combien te rapporte ton activité"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">✗</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#FDFCFF] to-white p-8 rounded-2xl border-2 border-[#897FCB]/20">
              <h3 className="text-2xl font-bold mb-6" style={{ color: COLORS.success }}>
                Avec Biz360, tu peux enfin...
              </h3>
              <ul className="space-y-4">
                {[
                  "Voir ton CA et tes cotisations calculées automatiquement",
                  "Être alerté(e) AVANT de dépasser un seuil important",
                  "Savoir exactement ce que tu dois déclarer chaque mois",
                  "Te concentrer sur ton métier, pas sur l'administratif"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">✓</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.primary }}>
              Tout ce dont tu as besoin, en un seul endroit
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Un tableau de bord pensé pour les auto-entrepreneurs, par un auto-entrepreneur.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border hover:border-[#897FCB] hover:shadow-lg transition-all group">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#210875] to-[#897FCB] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: COLORS.primary }}>{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── Section Co-brand Qonto ─────────── */}
      <section
        className="py-16 md:py-20 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #F8F5FF 0%, #FFF1E0 100%)" }}
        data-testid="qonto-landing-section"
      >
        {/* Décorations subtiles */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: "#897FCB" }} />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: "#E8A87C" }} />

        <div className="max-w-6xl mx-auto px-4 relative">
          {/* Bandeau marque */}
          <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
            <span className="font-bold text-2xl tracking-tight inline-flex items-center gap-1" style={{ color: COLORS.primary }}>
              Biz360 <span aria-hidden>❤️‍🔥</span>
            </span>
            <span className="text-2xl text-gray-400">×</span>
            <QontoLogo size={32} />
          </div>

          {!QONTO_ENABLED && (
            <div className="flex justify-center mb-4">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                style={{ backgroundColor: COLORS.primary, color: "white" }}
                data-testid="qonto-coming-soon-badge"
              >
                ✨ Bientôt disponible
              </span>
            </div>
          )}

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3" style={{ color: COLORS.primary }}>
            {QONTO_ENABLED ? (
              <>Tu utilises Qonto ? <span style={{ color: COLORS.coral }}>Zéro saisie.</span></>
            ) : (
              <>L'intégration Qonto arrive <span style={{ color: COLORS.coral }}>très bientôt.</span></>
            )}
          </h2>
          <p className="text-lg text-center text-gray-700 max-w-2xl mx-auto mb-12">
            {QONTO_ENABLED
              ? "Connecte ton compte pro Qonto en 1 clic. Tes factures clients et fournisseurs remontent automatiquement dans Biz360 — pendant que tu te concentres sur ton vrai métier."
              : "Notre partenariat avec Qonto est en cours de validation. Dès qu'il est signé, tes factures clients et fournisseurs remonteront automatiquement dans Biz360 — zéro saisie."}
          </p>

          {/* 3 bullets */}
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border p-5" style={{ borderColor: "#D6CCFF" }}>
              <div className="text-3xl mb-2">⚡</div>
              <h3 className="font-bold text-base mb-1" style={{ color: COLORS.primary }}>Temps réel</h3>
              <p className="text-sm text-gray-600">
                Une facture est créée dans Qonto ? Elle apparaît dans Biz360 en moins de 5 secondes, sans rafraîchir.
              </p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border p-5" style={{ borderColor: "#D6CCFF" }}>
              <div className="text-3xl mb-2">🤖</div>
              <h3 className="font-bold text-base mb-1" style={{ color: COLORS.primary }}>Plus de double saisie</h3>
              <p className="text-sm text-gray-600">
                Le CA, la TVA, les cotisations Urssaf se calculent automatiquement à partir de tes vraies factures.
              </p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border p-5" style={{ borderColor: "#D6CCFF" }}>
              <div className="text-3xl mb-2">🔐</div>
              <h3 className="font-bold text-base mb-1" style={{ color: COLORS.primary }}>RGPD & sécurisé</h3>
              <p className="text-sm text-gray-600">
                Lecture seule. Tokens chiffrés (AES-128). Consentement explicite. Déconnexion en 1 clic.
              </p>
            </div>
          </div>

          {/* Bandeau "Comment ça marche" */}
          <div className="bg-white rounded-2xl border p-5 sm:p-7 shadow-sm" style={{ borderColor: "#D6CCFF" }}>
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full text-white font-bold text-sm flex items-center justify-center" style={{ backgroundColor: COLORS.primary }}>1</div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: COLORS.primary }}>Je connecte Qonto</div>
                  <div className="text-xs text-gray-600 mt-0.5">OAuth officiel, en 30 secondes.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full text-white font-bold text-sm flex items-center justify-center" style={{ backgroundColor: COLORS.primary }}>2</div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: COLORS.primary }}>Je facture comme d'habitude</div>
                  <div className="text-xs text-gray-600 mt-0.5">Tu continues à utiliser Qonto pour facturer tes clients.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full text-white font-bold text-sm flex items-center justify-center" style={{ backgroundColor: COLORS.primary }}>3</div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: COLORS.primary }}>Biz360 fait le reste</div>
                  <div className="text-xs text-gray-600 mt-0.5">Pilotage, Urssaf, TVA, alertes — tout est automatique.</div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            {QONTO_ENABLED
              ? "Pas encore de Qonto ? Pas de souci, Biz360 fonctionne aussi avec une saisie manuelle classique."
              : "En attendant l'activation Qonto, Biz360 fonctionne dès aujourd'hui avec une saisie manuelle ou via import CSV."}
          </p>
        </div>
      </section>

      {/* ─────────── Section Essentiel du statut AE ─────────── */}
      <section
        id="essentiel-statut-ae"
        className="py-16 md:py-20 scroll-mt-20"
        style={{ backgroundColor: COLORS.bg }}
        data-testid="landing-essentiel-statut"
      >
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3"
              style={{ backgroundColor: COLORS.primary + "15", color: COLORS.primary }}
            >
              📚 Ressources pédagogiques
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: COLORS.primary }}>
              L'essentiel du statut auto-entrepreneur
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Avant de te lancer (ou tout au long de ton activité), prends 3 minutes pour
              comprendre les règles du jeu : création, plafonds, cotisations obligatoires,
              droit du travail.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              {
                title: "Création du statut",
                desc: "Démarches simplifiées via le Guichet Unique INPI.",
                anchor: "creation",
              },
              {
                title: "Cotisations obligatoires",
                desc: "Taux 2025-2026 et caractère impératif des cotisations.",
                anchor: "cotisations",
              },
              {
                title: "Rôle des cotisations",
                desc: "Maladie, retraite, formation, CSG — à quoi ça sert vraiment.",
                anchor: "role-cotisations",
              },
              {
                title: "Droit du travail",
                desc: "Statut d'indépendant, salariat déguisé, cumul emploi.",
                anchor: "droit-travail",
              },
            ].map((card) => (
              <a
                key={card.anchor}
                href={`/statut-auto-entrepreneur#${card.anchor}`}
                className="block p-5 rounded-xl bg-white border hover:shadow-md transition group"
                data-testid={`landing-statut-card-${card.anchor}`}
              >
                <div
                  className="font-semibold mb-2 group-hover:underline"
                  style={{ color: COLORS.primary }}
                >
                  {card.title}
                </div>
                <p className="text-sm text-gray-600 leading-snug">{card.desc}</p>
              </a>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/statut-auto-entrepreneur"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition hover:opacity-90"
              style={{ backgroundColor: COLORS.primary, color: "white" }}
              data-testid="landing-statut-cta-main"
            >
              Lire le guide complet
              <ChevronRight size={16} />
            </a>
            <a
              href="https://www.autoentrepreneur.urssaf.fr/portail/accueil.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm border transition hover:bg-white"
              style={{ borderColor: COLORS.primary, color: COLORS.primary }}
              data-testid="landing-urssaf-official-cta"
            >
              Site officiel Urssaf ↗
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.primary }}>
              Ils ont testé, ils adorent
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(j => <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-gray-700 mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#210875] to-[#897FCB] flex items-center justify-center text-white font-bold">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="font-semibold" style={{ color: COLORS.primary }}>{t.name}</div>
                    <div className="text-sm text-gray-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#210875] to-[#897FCB]">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm mb-6">
            <Zap className="w-4 h-4" />
            Un seul plan, tout inclus
          </div>

          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="text-[#E8A87C]">12€</span>/mois — sans engagement
          </h2>
          <p className="text-xl opacity-90 mb-8">
            30 jours d'essai gratuits, sans carte bancaire.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-lg mx-auto mb-8">
            <ul className="space-y-3 text-left">
              {[
                "Tableau de bord complet",
                "Calculs Urssaf automatiques",
                "Suivi des seuils TVA",
                "Alertes personnalisées",
                "Mon email direct (réponse 24h)",
                "Guide e-facturation 2027 offert"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#E8A87C]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-sm opacity-80 mb-6">
            🔒 Paiement sécurisé Stripe • Résiliation en 1 clic
          </p>

          <a
            href="/subscription"
            className="inline-block h-14 px-10 bg-[#059669] hover:bg-[#047857] text-white font-bold rounded-xl text-lg transition-all hover:scale-105 leading-[3.5rem]"
            data-testid="landing-pricing-cta"
          >
            Essayer 30 jours gratuits →
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.primary }}>
            Prêt·e à simplifier ta gestion ?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Découvre Biz360 dès aujourd'hui — l'outil de pilotage pensé pour les auto-entrepreneurs français.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="/subscription"
              className="inline-flex items-center justify-center h-14 px-8 bg-[#059669] hover:bg-[#047857] text-white font-bold rounded-xl transition-all hover:scale-105"
              data-testid="landing-final-cta"
            >
              Essayer 30 jours gratuits
            </a>
            <a
              href="/subscription"
              className="inline-flex items-center justify-center h-14 px-8 border-2 font-semibold rounded-xl transition hover:bg-gray-50"
              style={{ borderColor: COLORS.primary, color: COLORS.primary }}
              data-testid="landing-final-pricing-cta"
            >
              Voir tous les tarifs
            </a>
          </div>

          {/* Mini-form notif (optionnel — pas de faux chiffres) */}
          {!submitted ? (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto mt-10 pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-3">Pas prêt·e ? Reste informé·e des nouveautés :</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ton email"
                  required
                  className="flex-1 h-12 px-4 rounded-xl border-2 border-gray-200 focus:border-[#897FCB] focus:outline-none text-sm"
                />
                <button
                  type="submit"
                  className="h-12 px-6 bg-[#210875] hover:bg-[#897FCB] text-white font-semibold rounded-xl transition-all text-sm"
                  data-testid="landing-newsletter-submit"
                >
                  Je m'inscris
                </button>
              </div>
            </form>
          ) : (
            <p className="mt-10 text-sm text-emerald-600 font-medium" data-testid="landing-newsletter-confirmed">
              ✅ Merci ! Tu recevras nos prochaines actus par email.
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default LandingPage;
